import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabel } from 'src/Library/Types';
import {
  Modal,
  Form,
  Button,
  message,
  Row,
  Col,
  Select,
} from 'src/Library/AntdLibComponents';
import { FormComponentProps } from 'antd/lib/form';
import { uploadSIModalStyles } from './UploadSIModal.styles';
import isEmpty from 'lodash/isEmpty';
import { renderLabelAndInput, renderLabelAndInputNumber, renderSelectDropDown } from 'src/Library/Common';
import { useDebounce } from 'src/hooks/useDebounce';
import { useGenericState } from 'src/Library/UseGenericState';
import { searchCountry } from 'src/api/country';
import { isValidString, isValidNumber } from 'src/Library/Helper';
import { addShipmentInstructionsPartiesAddress } from 'src/api/shipmentExecution';

interface IProps extends StylesProps<ReturnType<typeof uploadSIModalStyles>>,
  FormComponentProps {
  isVisible: boolean;
  onCancel: () => void;
  editAddressData;
  onSubmit: () => void;
  shipmentId: string;
  partyType: string;
}

interface StateProps {
  isSearchingCountry: boolean;
  searchTerm?: string;
  countryList: KeyLabel [];
  isLoading?: boolean;
}

const UploadSINotifyPartiesAddAddress = (props: IProps): React.ReactElement => {

  const {
    classes,
    isVisible,
    onCancel,
    form,
    editAddressData,
    onSubmit,
    shipmentId,
    partyType,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    countryList: [],
    isSearchingCountry: false,
  });

  const {
    isSearchingCountry,
    searchTerm,
    countryList,
    isLoading,
  } = state;

  const debouncedSearchCountry = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    searchCountryList(debouncedSearchCountry);
  }, [debouncedSearchCountry]);

  const searchCountryList = async (queryString: string = '') => {
    setState({ isSearchingCountry: true });
    let countryList : KeyLabel [] = [];
    const response = await searchCountry({ queryString });
    if (response.isSuccess) {
      countryList = response.data.map(country => ({
        key: country.id,
        label: country.countryName,
      }))
    }
    setState({ countryList, isSearchingCountry: false });
  };

  const handleClear = () : void => {
    form.setFieldsValue({
      street: undefined,
      city: undefined,
      state: undefined,
      pincode: undefined,
      country: undefined,
    });
  }

  const renderFooter = () : React.ReactElement => {
    const isNotDisabled = Object.values(form.getFieldsValue())?.some(
      item => isValidString(item) || isValidNumber(item) || !isEmpty(item))
    return (
      <div className={classes.justifySpaceBetween}>
        <Button
          className={classes.clearButton}
          size="large"
          onClick={() => handleClear()}
        >Clear</Button>
        <Button
          className={classes.addAddressButton}
          size="large"
          disabled={!isNotDisabled}
          onClick={() => handleSubmit()}
          loading={isLoading}
        >
          {isEmpty(editAddressData) ? 'Add' : 'Update'}
        </Button>
      </div>
    );
  };

  const handleSubmit = () => {
    form.validateFieldsAndScroll(async(err, values) => {
      if (err) {
        return;
      }
      setState({ isLoading: true });
      const body = {
        ...values,
        partyType,
        countryId: values?.countryId?.key,
      }
      const response = await addShipmentInstructionsPartiesAddress(
        editAddressData?.id,
        body,
      );
      if (response.isSuccess) {
        onSubmit();
        message.success(`Address ${isEmpty(editAddressData) ? 'Added' : 'Updated'} Successfully`);
        setState({ isLoading: false });
      } else {
        message.error(response.errorMessage);
        setState({ isLoading: false });
      }
    });
  }

  const rennderFormFields = () : React.ReactElement => {
    const labelInputProps = {
      colSpan: {
        left: 6,
        right: 18,
      },
      form,
      classes,
    };
    return (
      <React.Fragment>
        <Row>
          <Col span={11}>
            {renderLabelAndInput({
              ...labelInputProps,
              label: 'Street',
              key: 'street',
              initialValue: editAddressData?.street,
              placeholder: 'Enter Street',
              rules: [{ max: 35, message: 'Maximum 35 characters allowed' }],
            })}
          </Col>
          <Col span={2} />
          <Col span={11}>
            {renderLabelAndInput({
              ...labelInputProps,
              label: 'City',
              key: 'city',
              initialValue: editAddressData?.city,
              placeholder: 'Enter City',
              rules: [{ max: 35, message: 'Maximum 35 characters allowed' }],
            })}
          </Col>
        </Row>
        <Row>
          <Col span={11}>
            {renderLabelAndInput({
              ...labelInputProps,
              label: 'State/ Province',
              key: 'state',
              initialValue: editAddressData?.state,
              placeholder: 'Enter State/Province',
              rules: [{ max: 9, message: 'Maximum 9 characters allowed' }],
            })}
          </Col>
          <Col span={2} />
          <Col span={11}>
            {renderLabelAndInput({
              ...labelInputProps,
              label: 'Postal Code*',
              key: 'pincode',
              rules: [
                { required: true, message: 'Required' },
                { max: 17, message: 'Maximum 17 characters allowed' },
              ],
              initialValue: editAddressData?.pincode,
              placeholder: 'Enter Postal Code',
            })}
          </Col>
        </Row>
        <Row>
          <Col span={11}>
            <Col span={6} className={classes.label}>
              Country
            </Col>
            <Col span={18}>
              <Form.Item>
                {form.getFieldDecorator('countryId', {
                  initialValue: editAddressData?.country?.id ? {
                    key: editAddressData?.country?.id,
                    label: editAddressData?.country?.countryName,
                  } : undefined,
                })(
                  <Select
                    getPopupContainer={(trigger: any) => trigger.parentNode}
                    showSearch
                    className={classes.selectField}
                    placeholder="Country"
                    onSearch={val => setState({ searchTerm: val })}
                    onFocus={() => setState({ searchTerm: '' })}
                    allowClear
                    labelInValue
                    filterOption={(input, option) => true}
                    dropdownMatchSelectWidth={false}
                    dropdownRender={menu => renderSelectDropDown(menu, isSearchingCountry)}>
                    {countryList?.map(item => (
                      <Select.Option key={item.key}>{item.label}</Select.Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Col>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  return (
    <Modal
      visible={isVisible}
      zIndex={1200}
      width={780}
      title={`${isEmpty(editAddressData) ? 'Add' : 'Edit'} Address`}
      onCancel={onCancel}
      footer={renderFooter()}
    >
      {rennderFormFields()}
    </Modal>
  );
};
export default React.memo(withStyles(uploadSIModalStyles)(Form.create()(UploadSINotifyPartiesAddAddress)));